import React, { useEffect, useRef, useState } from 'react';

/**
 * Parses a stat string like "165M+", "100%", "2x", "5", "£0"
 * and returns { prefix, number, suffix } for animation.
 */
const parseValue = (raw) => {
  const str = String(raw).trim();
  // match leading non-digit prefix, the number, and trailing suffix
  const m = str.match(/^([^\d.-]*)(-?\d+(?:\.\d+)?)(.*)$/);
  if (!m) return { prefix: '', number: null, suffix: str };
  return {
    prefix: m[1] || '',
    number: parseFloat(m[2]),
    suffix: m[3] || '',
  };
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * CountUp component - animates a stat value up when it scrolls into view.
 * Preserves prefix/suffix (e.g. £, M+, %, x) exactly.
 */
const CountUp = ({ value, duration = 1600, className = '' }) => {
  const { prefix, number, suffix } = parseValue(value);
  const ref = useRef(null);
  const [display, setDisplay] = useState(number === null ? value : prefix + '0' + suffix);
  const startedRef = useRef(false);

  useEffect(() => {
    if (number === null) {
      setDisplay(value);
      return;
    }
    const node = ref.current;
    if (!node) return;

    const runAnimation = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const step = (now) => {
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(t);
        const current = number * eased;
        // preserve decimals if the target has decimals; otherwise integer
        const hasDecimal = !Number.isInteger(number);
        const formatted = hasDecimal
          ? current.toFixed(1)
          : Math.round(current).toString();
        setDisplay(prefix + formatted + suffix);
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runAnimation();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [number, prefix, suffix, duration, value]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

export default CountUp;
